// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Line endings, which are not a detail.
 *
 * A model answers in LF. Half the world's repositories are checked out with
 * CRLF. Diffing one against the other with no normalisation produces, measured
 * on a 5,000-line file whose text is visually identical: ONE hunk of TEN
 * THOUSAND lines, the entire file rewritten, in about 300 ms per 2,000 lines.
 *
 * That is worse than it looks. The O(ND) cost of a diff is its edit distance, so
 * a 1%-changed file takes 4 ms and a 100%-rewritten one takes two seconds. CRLF
 * mismatch converts every ordinary case into the pathological one, on a change
 * that carries no information at all — and the review UI then asks the user to
 * page through 10,000 lines of nothing.
 *
 * So: normalise both sides to LF before diffing, remember what the file on disk
 * was actually using, and put it back on the way out. A CRLF repository stays a
 * CRLF repository.
 */

export type Eol = '\n' | '\r\n';

/**
 * The file's dominant line ending.
 *
 * Dominant rather than "any", because a file can contain a stray `\r\n` inside a
 * string literal or a fixture without being a CRLF file. Ties go to LF: a file
 * with no line endings at all is not a CRLF file, and neither is an empty one.
 */
export function detectEol(text: string): Eol {
  let crlf = 0;
  let lf = 0;
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) !== 10) continue;
    if (i > 0 && text.charCodeAt(i - 1) === 13) crlf++;
    else lf++;
  }
  return crlf > lf ? '\r\n' : '\n';
}

/** Everything downstream of this — diff, hunks, patch — sees LF only. */
export function toLf(text: string): string {
  return text.replace(/\r\n/g, '\n');
}

/**
 * Restore the file's own convention immediately before the bytes are written.
 *
 * Normalises FIRST in both branches. Returning the input untouched for the LF
 * case looks like an obvious short-circuit and is wrong: text arriving with CRLF
 * in it would then be written to a file the tool has just decided is LF.
 */
export function restoreEol(text: string, eol: Eol): string {
  const lf = toLf(text);
  return eol === '\n' ? lf : lf.replace(/\n/g, '\r\n');
}
